/**
 * The product's mark: the Loop in glass tiles, from the identity kit in
 * brand/logo (board: brand/logo/kit/board/gojibasel-board.html).
 *
 * One line enters, loops once and leaves, built from isometric glass blocks
 * that run from blue to violet. The kit's rule for where each cut goes, which
 * these three components follow:
 *
 * - `Logo`, the SMALL CUT, for anything under 48px. A chunkier geometry that
 *   keeps the stem visible down to 16px. Drawn inline so it is crisp at every
 *   size.
 * - `LogoSymbol`, the full symbol, for 48px and up (app icons, avatars, an
 *   empty state). The kit file is 31KB of facets, so it is an image from
 *   public/brand rather than markup in the bundle.
 * - `LogoLockup`, symbol plus wordmark, the default wherever the product names
 *   itself (the sign-in screen).
 *
 * The colours are the kit's, not tokens: a logo does not change with the
 * palette, only with the ground.
 *
 * THE KIT SHIPS TWO COLOURINGS AND THIS RENDERS ONE. The console is light on
 * every machine now (the owner, 2026-10-06: "remove the dark mode from the app
 * and the auto theme selection based on os"), so there is one ground and the
 * mark takes the light cut. The dark cut stays in the kit and stays copied into
 * public/brand, because it is identity material rather than a theme: it is what
 * the mark is on a dark ground wherever one exists, a deck, a dark email, a
 * favicon on a dark tab strip. logo.test.ts still compares both files with the
 * kit so neither copy can drift, and the inline cut against the light one.
 */
import type { ImgHTMLAttributes, SVGProps } from 'react'

/** The small cut's facets, drawn in order, with the fill of each. */
const SMALL_CUT: readonly { d: string; light: string }[] = [
  { d: 'M192.11 198.32L192.11 87.29L128 50.27L128 37.94L106.63 25.6L63.89 50.27L63.89 161.31L128 198.32L128 210.66L149.37 223ZM149.37 136.64L149.37 111.96L128 99.62L106.63 111.96L106.63 136.64L128 148.97Z', light: 'fill-[#6C49F4]' },
  { d: 'M170.74 185.99L160.05 179.82L138.68 192.15L149.37 198.32ZM128 62.61L128 50.27L192.11 87.29L170.74 99.62L128 74.95L106.63 87.29L106.63 74.95ZM170.74 148.97L149.37 136.64L128 148.97L106.63 136.64L85.26 148.97L128 173.65ZM128 37.94L106.63 25.6L63.89 50.27L85.26 62.61Z', light: 'fill-[#94A2FF]' },
  { d: 'M160.05 179.82L170.74 185.99L170.74 173.65ZM128 74.95L170.74 99.62L170.74 148.97L149.37 136.64L149.37 111.96L128 99.62ZM149.37 198.32L138.68 192.15L128 198.32L128 210.66L149.37 223ZM85.26 148.97L85.26 62.61L63.89 50.27L63.89 161.31L128 198.32L128 173.65Z', light: 'fill-[#6C49F4]' },
  { d: 'M170.74 185.99L170.74 173.65L128 198.32L128 173.65L170.74 148.97L170.74 99.62L192.11 87.29L192.11 198.32L149.37 223L149.37 198.32ZM128 74.95L106.63 87.29L106.63 74.95L128 62.61L128 37.94L85.26 62.61L85.26 148.97L106.63 136.64L106.63 111.96L128 99.62Z', light: 'fill-[#2C2A9C]' },
]

/** The small cut, under 48px. Its own colours, so it needs no tile behind it. */
export function Logo({ className, ...props }: Omit<SVGProps<SVGSVGElement>, 'children'>): JSX.Element {
  return (
    <svg viewBox="63.39 25.1 129.21 198.4" aria-hidden="true" className={className} {...props}>
      {SMALL_CUT.map((layer) => (
        <path key={layer.d} d={layer.d} fillRule="evenodd" className={layer.light} />
      ))}
    </svg>
  )
}

type BrandImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> & {
  /** Read out instead of the image. Empty by default, because the mark sits beside the name. */
  alt?: string
}

/**
 * One kit file, rendered as one image.
 *
 * This used to be two `<img>` elements, the light one hidden under `dark:` and
 * the dark one shown by it, which is also two requests for one mark on every
 * page. A single ground means a single image.
 */
function BrandImage({ file, alt = '', className, ...props }: BrandImageProps & { file: string }): JSX.Element {
  return <img src={`/brand/${file}-light.svg`} alt={alt} className={className} {...props} />
}

/** The full symbol, 48px and up. Size it by height, the width follows. */
export function LogoSymbol(props: BrandImageProps): JSX.Element {
  return <BrandImage file="gojibasel-symbol" {...props} />
}

/** The horizontal lockup, symbol and wordmark. Size it by height, the width follows. */
export function LogoLockup({ alt = 'Son of Basel', ...props }: BrandImageProps): JSX.Element {
  return <BrandImage file="gojibasel-horizontal" alt={alt} {...props} />
}
